import React from "react";
import { useTranslation } from "react-i18next";
import { CalendarX, CircleDollarSign, ShoppingBag } from "lucide-react";
import {
  StatsSummaryBlock,
  type StatsSummaryCard,
} from "@/components/common/StatsSummaryBlock";
import { formatCurrencyAmount } from "@/components/pages/services/utils/priceFormatting";
import { useServiceSummaryStats } from "../hooks/useServiceSummaryStats";

interface ServicesStatsProps {
  currency?: string;
}

const badgeClassName =
  "inline-flex max-w-full w-fit items-center justify-center rounded-md bg-royal-glow-5 px-2 py-0.5 text-2xs font-semibold leading-none text-royal-glow-2";

const formatValue = (
  isLoading: boolean,
  value: React.ReactNode | null | undefined,
): React.ReactNode => {
  if (isLoading) {
    return "…";
  }
  return value ?? "—";
};

const formatLabel = (
  isLoading: boolean,
  name: string | null | undefined,
): string => {
  if (isLoading) {
    return "…";
  }
  return name ?? "—";
};

const ServicesStats: React.FC<ServicesStatsProps> = ({ currency }) => {
  const { t } = useTranslation();
  const { highestRevenue, mostOrdered, mostCanceled, isLoading } =
    useServiceSummaryStats();

  const cards: StatsSummaryCard[] = [
    {
      key: "highestRevenue",
      label: formatLabel(isLoading, highestRevenue?.name),
      value: formatValue(
        isLoading,
        highestRevenue
          ? formatCurrencyAmount(highestRevenue.revenue, currency)
          : null,
      ),
      icon: CircleDollarSign,
      valueClassName: "text-green-600",
      badge: (
        <span className={badgeClassName}>
          {t("services.stats.highestRevenue", "Highest Revenue")}
        </span>
      ),
    },
    {
      key: "mostOrdered",
      label: formatLabel(isLoading, mostOrdered?.name),
      value: formatValue(isLoading, mostOrdered?.bookings),
      icon: ShoppingBag,
      valueClassName: "text-monochrome-core-1",
      badge: (
        <span className={badgeClassName}>
          {t("services.stats.mostOrdered", "Most Ordered")}
        </span>
      ),
    },
    {
      key: "mostCanceled",
      label: formatLabel(isLoading, mostCanceled?.name),
      value: formatValue(isLoading, mostCanceled?.canceled),
      icon: CalendarX,
      valueClassName: "text-red-600",
      badge: (
        <span className={badgeClassName}>
          {t("services.stats.mostCanceled", "Most Canceled")}
        </span>
      ),
    },
  ];

  return <StatsSummaryBlock cards={cards} />;
};

export default ServicesStats;
